// Client-side ecommerce analytics facade.
//
// One call fans out to BOTH GA4 (window.gtag) and Meta Pixel (window.fbq),
// plus Google Ads conversion on purchase. Every sink is feature-detected, so
// when a tag isn't loaded (env var unset) the call is a safe no-op — B-bucket
// tags stay fully silent until their IDs are provided.
//
// transaction_id / Meta eventID are both the order number, so GA4 and Meta
// each de-duplicate a purchase that arrives from the browser AND the server
// (Measurement Protocol / Conversions API). See lib/serverAnalytics.js.

export const CURRENCY = "TWD";

// Google Ads conversion target, e.g. "AW-1234567890/AbCdEf…". Both halves come
// from the client env; when either is missing we skip the Ads conversion.
const ADS_ID = process.env.NEXT_PUBLIC_GOOGLE_ADS_ID || "";
const ADS_PURCHASE_LABEL =
  process.env.NEXT_PUBLIC_GOOGLE_ADS_PURCHASE_LABEL || "";

// ---- Pure payload builders (exported for unit tests) ----

/** Normalize a cart/artwork item to a GA4 `items[]` entry. */
export const toGa4Item = (item = {}) => ({
  item_id: String(item.id ?? ""),
  item_name: item.name ?? "",
  price: Number(item.price) > 0 ? Number(item.price) : 0,
  quantity: 1,
});

/** Sum resolved item prices. Accepts an explicit fallback unit price. */
export const sumItemsValue = (items = [], fallbackUnitPrice = 0) =>
  (items || []).reduce((acc, it) => {
    const p = Number(it?.price) > 0 ? Number(it.price) : Number(fallbackUnitPrice) || 0;
    return acc + p;
  }, 0);

export const buildViewItemPayload = (item, value) => ({
  currency: CURRENCY,
  value: Number(value) || 0,
  items: [toGa4Item(item)],
});

export const buildAddToCartPayload = (item, value) => ({
  currency: CURRENCY,
  value: Number(value) || 0,
  items: [toGa4Item(item)],
});

export const buildBeginCheckoutPayload = (items, value) => ({
  currency: CURRENCY,
  value: Number(value) || 0,
  items: (items || []).map(toGa4Item),
});

export const buildPurchasePayload = ({ orderNo, value, items } = {}) => ({
  transaction_id: String(orderNo ?? ""),
  currency: CURRENCY,
  value: Number(value) || 0,
  items: (items || []).map(toGa4Item),
});

/** Content ids for Meta Pixel (content_ids expects strings). */
const contentIds = (items = []) =>
  (items || []).map((it) => String(it?.id ?? "")).filter(Boolean);

// ---- Sinks (feature-detected, SSR-safe) ----

const ga = (event, params) => {
  if (typeof window === "undefined" || typeof window.gtag !== "function") return;
  window.gtag("event", event, params);
};

const meta = (event, params, opts) => {
  if (typeof window === "undefined" || typeof window.fbq !== "function") return;
  if (opts) window.fbq("track", event, params, opts);
  else window.fbq("track", event, params);
};

// ---- Purchase de-dup guard (browser refresh shouldn't double-count) ----

const purchaseKey = (orderNo) => `meowmi-tracked-purchase-${orderNo}`;

export const hasTrackedPurchase = (orderNo) => {
  if (typeof window === "undefined" || !orderNo) return false;
  try {
    return window.localStorage.getItem(purchaseKey(orderNo)) === "1";
  } catch {
    return false;
  }
};

const markPurchaseTracked = (orderNo) => {
  if (typeof window === "undefined" || !orderNo) return;
  try {
    window.localStorage.setItem(purchaseKey(orderNo), "1");
  } catch {
    /* private mode / quota — worst case a refresh re-fires; GA4 + Meta still
       de-dup by transaction_id / eventID, so this is best-effort only. */
  }
};

// ---- Public event API ----

export const trackViewItem = (item, value) => {
  const v = Number(value) > 0 ? Number(value) : toGa4Item(item).price;
  ga("view_item", buildViewItemPayload(item, v));
  meta("ViewContent", {
    content_ids: contentIds([item]),
    content_type: "product",
    value: Number(v) || 0,
    currency: CURRENCY,
  });
};

export const trackAddToCart = (item, value) => {
  const v = Number(value) > 0 ? Number(value) : toGa4Item(item).price;
  ga("add_to_cart", buildAddToCartPayload(item, v));
  meta("AddToCart", {
    content_ids: contentIds([item]),
    content_type: "product",
    value: Number(v) || 0,
    currency: CURRENCY,
  });
};

export const trackBeginCheckout = (items, value) => {
  ga("begin_checkout", buildBeginCheckoutPayload(items, value));
  meta("InitiateCheckout", {
    content_ids: contentIds(items),
    content_type: "product",
    num_items: (items || []).length,
    value: Number(value) || 0,
    currency: CURRENCY,
  });
};

export const trackPurchase = ({ orderNo, value, items } = {}) => {
  if (!orderNo || hasTrackedPurchase(orderNo)) return;
  markPurchaseTracked(orderNo);

  ga("purchase", buildPurchasePayload({ orderNo, value, items }));

  meta(
    "Purchase",
    {
      content_ids: contentIds(items),
      content_type: "product",
      value: Number(value) || 0,
      currency: CURRENCY,
    },
    { eventID: String(orderNo) },
  );

  // Google Ads conversion (only when both ID and label are configured).
  if (ADS_ID && ADS_PURCHASE_LABEL) {
    ga("conversion", {
      send_to: `${ADS_ID}/${ADS_PURCHASE_LABEL}`,
      transaction_id: String(orderNo),
      value: Number(value) || 0,
      currency: CURRENCY,
    });
  }
};
